import { computed, inject, Injectable, Signal } from '@angular/core';
import { DomainErrorKind } from '@core/http/errors/domain-errors';
import { SiteFacade } from '@core/data/site/site-facade';
import { SiteVm } from '@core/data/site/site.model';
import { LocationFacade } from '@core/data/location/location-facade';

@Injectable({ providedIn: 'root' })
export class SiteDashboardFacade {
  private readonly sitesFacade = inject(SiteFacade);
  private readonly locationFacade = inject(LocationFacade);

  public readonly vm: Signal<SiteVm> = computed(() => {
    const site = this.sitesFacade.activeSite();
    const status = !site ? 'error' : this.sitesFacade.state();
    let error = this.sitesFacade.error();

    error = !error && !site ? this.toErrorMessage('not-found') : error;

    return {
      status: status,
      siteId: site?.id ?? null,
      siteName: site?.name ?? null,
      locations: site?.locations ?? [],
      error: error,
    };
  });

  /** Site name as the toolbar title, falls back to site ID while loading. */
  readonly toolbarTitle: Signal<string> = computed(() => this.vm().siteName ?? 'Site');

  openLocation(locationId: number): void {
    this.locationFacade.openLocation(this.sitesFacade.activeSiteId() || 0, locationId);
  }

  private toErrorMessage(errorKind: DomainErrorKind): string {
    if (errorKind === 'auth') {
      return 'Authentication is required to view this site.';
    }
    if (errorKind === 'forbidden') {
      return 'You do not have permission to view this site.';
    }
    if (errorKind === 'not-found') {
      return 'The requested site could not be found.';
    }
    if (errorKind === 'server') {
      return 'Site location service is unavailable. Please try again.';
    }
    return 'Unable to load site details. Please try again.';
  }
}
